import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { validatePasswordResetToken, confirmPasswordReset } from "../api/AuthAPI";
import { getUserMessage, logApiError } from "../api/apiError";

const ResetPasswordForm: React.FC = () => {
    const navigate = useNavigate();

    const [token, setToken]               = useState<string | null>(null);
    const [email, setEmail]               = useState<string | null>(null);
    const [tokenError, setTokenError]     = useState<string | null>(null);
    const [validating, setValidating]     = useState(true);

    const [password, setPassword]         = useState("");
    const [confirm, setConfirm]           = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState<string | null>(null);
    const [success, setSuccess]           = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get("token");
        if (!t) {
            setTokenError("Missing reset token. Please use the link from your email.");
            setValidating(false);
            return;
        }
        setToken(t);
        validatePasswordResetToken(t)
            .then(({ email }) => setEmail(email))
            .catch(err => {
                logApiError(err);
                setTokenError(getUserMessage(err));
            })
            .finally(() => setValidating(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        if (!token) return;

        setLoading(true);
        try {
            await confirmPasswordReset(token, password);
            setSuccess(true);
            setTimeout(() => navigate("/"), 3000);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <div className="auth-card">
                <p className="subtitle">Validating reset link…</p>
            </div>
        );
    }

    if (tokenError) {
        return (
            <div className="auth-card">
                <h2>Invalid Link</h2>
                <p className="error-message">{tokenError}</p>
                <Link to="/forgot-password" className="btn-outline" style={{ marginTop: "1rem", display: "inline-block" }}>
                    Request a new reset link
                </Link>
            </div>
        );
    }

    if (success) {
        return (
            <div className="auth-card">
                <h2>Password Updated</h2>
                <p className="subtitle">Your password has been changed. Redirecting to sign in…</p>
                <Link to="/" className="btn-outline" style={{ marginTop: "1rem", display: "inline-block" }}>
                    Sign In Now
                </Link>
            </div>
        );
    }

    const inputType = showPassword ? "text" : "password";

    return (
        <div className="auth-card">
            <h2>Set New Password</h2>
            {email && (
                <p className="subtitle">
                    Resetting password for <strong>{email}</strong>
                </p>
            )}
            <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                    <label htmlFor="password">New Password</label>
                    <input
                        type={inputType}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        required
                        disabled={loading}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="confirm">Confirm Password</label>
                    <input
                        type={inputType}
                        id="confirm"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Re-enter new password"
                        required
                        disabled={loading}
                    />
                </div>
                <div>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", userSelect: "none" }}>
                        <input
                            type="checkbox"
                            checked={showPassword}
                            onChange={(e) => setShowPassword(e.target.checked)}
                            style={{ width: "auto" }}
                        />
                        Show password
                    </label>
                </div>
                <button type="submit" disabled={loading || !password.trim() || !confirm.trim()}>
                    {loading ? "Saving…" : "Set Password"}
                </button>
                {error && <p className="error-message">{error}</p>}
            </form>
        </div>
    );
};

export default ResetPasswordForm;
